import type { CreditNote } from "../../services/financialService";
import type { ExportColumn } from "../../utils/exportTable";

/** Where a credit note is in its approval: the workflow status, falling back to the older status. */
export const creditNoteStage = (note: CreditNote) => note.workflow_status || note.status;

export const CREDIT_STAGE_LABELS: Record<string, string> = {
  draft: "Draft",
  returned: "Returned for changes",
  submitted: "Awaiting review",
  verified: "Awaiting approval",
  approved: "Approved",
  posted: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

export const CREDIT_STAGE_DOTS: Record<string, string> = {
  draft: "bg-gray-400",
  returned: "bg-amber-500",
  submitted: "bg-sky-500",
  verified: "bg-indigo-500",
  approved: "bg-emerald-500",
  posted: "bg-emerald-500",
  rejected: "bg-red-500",
  cancelled: "bg-gray-400",
};

/** The switch above the register: each view is a set of stages. */
export const CREDIT_VIEWS = {
  all: { label: "All", stages: [] as string[] },
  preparing: { label: "Drafts & returned", stages: ["draft", "returned"] },
  review: { label: "Awaiting review", stages: ["submitted"] },
  approval: { label: "Awaiting approval", stages: ["verified"] },
  approved: { label: "Approved", stages: ["approved", "posted"] },
  rejected: { label: "Rejected", stages: ["rejected", "cancelled"] },
} as const;
export type CreditView = keyof typeof CREDIT_VIEWS;

export const CREDIT_ORDERINGS = {
  "-created_at": "Newest first",
  created_at: "Oldest first",
  "-amount": "Amount (highest first)",
  amount: "Amount (lowest first)",
} as const;

export const CREDIT_PAGE_SIZES = [10, 25, 50];

export interface CreditNoteFilters {
  search: string;
  view: CreditView;
  /** Only notes this user can move on: review or approve someone else's note. */
  needsMe: boolean;
  preparedFrom: string;
  preparedTo: string;
  ordering: keyof typeof CREDIT_ORDERINGS;
}

export const EMPTY_CREDIT_FILTERS: CreditNoteFilters = {
  search: "",
  view: "all",
  needsMe: false,
  preparedFrom: "",
  preparedTo: "",
  ordering: "-created_at",
};

/** Who is looking, and what they may do. */
export interface CreditActor {
  userId?: number;
  canReview: boolean;
  canApprove: boolean;
}

/** Whether this user is the next step for a note: nobody reviews or approves their own. */
export function creditNeedsActor(note: CreditNote, actor: CreditActor) {
  if (note.prepared_by === actor.userId) return false;
  const stage = creditNoteStage(note);
  return (stage === "submitted" && actor.canReview) || (stage === "verified" && actor.canApprove);
}

export const creditStageLabel = (note: CreditNote) => CREDIT_STAGE_LABELS[creditNoteStage(note)] ?? creditNoteStage(note);

export const creditPreparedOn = (note: CreditNote) => note.created_at?.slice(0, 10) ?? "";

export const countCreditFilters = (filters: CreditNoteFilters) =>
  [filters.search.trim(), filters.needsMe, filters.preparedFrom || filters.preparedTo].filter(Boolean).length;

function matchesNonView(note: CreditNote, filters: CreditNoteFilters, actor: CreditActor) {
  const search = filters.search.trim().toLowerCase();
  if (
    search &&
    ![note.credit_number, note.invoice_number, note.student_name, note.reason, note.prepared_by_name]
      .some((value) => value?.toLowerCase().includes(search))
  ) {
    return false;
  }
  if (filters.needsMe && !creditNeedsActor(note, actor)) return false;
  const day = creditPreparedOn(note);
  if (filters.preparedFrom && (!day || day < filters.preparedFrom)) return false;
  if (filters.preparedTo && (!day || day > filters.preparedTo)) return false;
  return true;
}

const inView = (note: CreditNote, view: CreditView) => {
  const stages: readonly string[] = CREDIT_VIEWS[view].stages;
  return !stages.length || stages.includes(creditNoteStage(note));
};

export function creditViewCounts(notes: CreditNote[], filters: CreditNoteFilters, actor: CreditActor) {
  const matching = notes.filter((note) => matchesNonView(note, filters, actor));
  return Object.fromEntries(
    (Object.keys(CREDIT_VIEWS) as CreditView[]).map((view) => [view, matching.filter((note) => inView(note, view)).length]),
  ) as Record<CreditView, number>;
}

export function filterCreditNotes(notes: CreditNote[], filters: CreditNoteFilters, actor: CreditActor): CreditNote[] {
  const rows = notes.filter((note) => matchesNonView(note, filters, actor) && inView(note, filters.view));
  const byDate = (a: CreditNote, b: CreditNote) => (a.created_at ?? "").localeCompare(b.created_at ?? "") || a.id - b.id;
  const compare: Record<CreditNoteFilters["ordering"], (a: CreditNote, b: CreditNote) => number> = {
    "-created_at": (a, b) => byDate(b, a),
    created_at: byDate,
    "-amount": (a, b) => Number(b.amount) - Number(a.amount) || byDate(b, a),
    amount: (a, b) => Number(a.amount) - Number(b.amount) || byDate(b, a),
  };
  return rows.sort(compare[filters.ordering]);
}

export function creditNoteExportColumns(): ExportColumn<CreditNote>[] {
  return [
    { header: "Credit note", accessor: (note) => note.credit_number },
    { header: "Prepared on", accessor: creditPreparedOn },
    { header: "Student", accessor: (note) => note.student_name },
    { header: "Invoice", accessor: (note) => note.invoice_number },
    { header: "Amount (UGX)", accessor: (note) => Number(note.amount) },
    { header: "Stage", accessor: creditStageLabel },
    { header: "Prepared by", accessor: (note) => note.prepared_by_name },
    { header: "Reviewed by", accessor: (note) => note.reviewed_by_name || "" },
    { header: "Approved by", accessor: (note) => note.approved_by_name || "" },
    { header: "Reason", accessor: (note) => note.reason },
    { header: "Rejection reason", accessor: (note) => note.rejection_reason || "" },
  ];
}
